import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 12000;

const logError = (label, error) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(label, error);
  }
};

/** Polls the dashboard's backend endpoints every POLL_INTERVAL_MS and derives bot metrics. */
export function useDashboardData() {
  const [stats, setStats] = useState(null);
  const [bot, setBot] = useState(null);
  const [squawkBot, setSquawkBot] = useState(null);
  const [chat, setChat] = useState([]);

  const load = useCallback(async () => {
    const [statsRes, botRes, squawkRes, chatRes] = await Promise.allSettled([
      axios.get(`${API}/stats`),
      axios.get(`${API}/bot/status`),
      axios.get(`${API}/bot/squawk/status`),
      axios.get(`${API}/chat/recent?limit=12`),
    ]);
    if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
    else logError("stats load failed", statsRes.reason);
    if (botRes.status === "fulfilled") setBot(botRes.value.data);
    else logError("bot status load failed", botRes.reason);
    if (squawkRes.status === "fulfilled") setSquawkBot(squawkRes.value.data);
    else logError("squawk bot status load failed", squawkRes.reason);
    if (chatRes.status === "fulfilled") setChat(chatRes.value.data);
    else logError("chat load failed", chatRes.reason);
    // API/axios are stable module-level imports; statsRes/botRes/squawkRes/chatRes are local
    // to this call — none of them belong in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const intervalId = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // POLL_INTERVAL_MS is a stable module constant and intervalId is local to this effect —
    // neither belongs in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const online = Boolean(bot?.online) && !bot?.error;
  const squawkOnline = Boolean(squawkBot?.online);

  const squawkMetrics = useMemo(() => [
    { slug: "servers", label: "Servers", value: squawkBot?.guildCount },
    { slug: "members", label: "Members", value: squawkBot?.memberCount },
    { slug: "7700-alerts", label: "7700 Alerts Sent", value: squawkBot?.alerts7700 },
    { slug: "7600-alerts", label: "7600 Alerts Sent", value: squawkBot?.alerts7600 },
    { slug: "commands-used", label: "Commands Used", value: squawkBot?.commandsUsed },
  ], [
    squawkBot?.guildCount, squawkBot?.memberCount,
    squawkBot?.alerts7700, squawkBot?.alerts7600, squawkBot?.commandsUsed,
  ]);

  const airlineMetrics = useMemo(() => [
    { slug: "servers", label: "Servers", value: bot?.guild_count },
    { slug: "members", label: "Members", value: bot?.member_count },
  ], [bot?.guild_count, bot?.member_count]);

  return { stats, bot, squawkBot, chat, online, squawkOnline, squawkMetrics, airlineMetrics };
}
